import React, { useState } from 'react';

import Grid from '@mui/material/Grid2';

import { 
  Character,
  CharacterFeature,
  CharacterLanguageFeature,
  CharacterSense,
  Proficiency,
  CharacterFeatureSource,
  SpellsFromList,
  SFFeature,
  LanguageFeature,
  Selectable,
  BonusSpells,
  CharacterBonusSpells,
} from "@dndModels";

import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
// import DataUtilities from '../../../utilities/common_utilities';

import CharacterASIBaseFeatureInput from "@dndComponents/character/features/CharacterASIBaseFeature";
import CharacterLanguageFeatureInput from "@dndComponents/character/misc/CharacterLanguageFeatureInput";
import CharacterEldritchInvocationsBox from "@dndComponents/character/misc/CharacterEldritchInvocationsBox";
import CharacterFightingStyleBox from "@dndComponents/character/misc/CharacterFightingStyleBox";
import CharacterSpecialFeatureBox from "@dndComponents/character/misc/CharacterSpecialFeatureBox";
import CharacterPactBoonBox from "@dndComponents/character/misc/CharacterPactBoonBox";
import CharacterFeatBox from "@dndComponents/character/feats/CharacterFeatBox";
import CharacterProficienciesInput from "@dndComponents/character/features/CharacterProficienciesInput";
import CharacterCharacteristicInput from "@dndComponents/character/features/CharacterCharacteristicInput";

import CharacterFeatureSourceInput from "@dndComponents/character/features/CharacterFeatureSourceInput";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import CharacterSelectableInput from "@dndComponents/character/features/CharacterSelectableInput";
import CharacterWeaponMasteriesBox from "@dndComponents/character/misc/CharacterWeaponMasteriesBox";
import CharacterKnowledgeFeatureInput from "@dndComponents/character/knowledge/CharacterKnowledgeFeatureInput";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import CharacterSubclassInput from "@dndComponents/character/misc/CharacterSubclassInput";
import CharacterSubraceInput from "@dndComponents/character/misc/CharacterSubraceInput";
import CharacterSpellsFromListInput from "@dndComponents/character/features/CharacterSpellsFromListInput";
import CharacterBonusSpellsInput from "@dndComponents/character/features/CharacterBonusSpellsInput";
import CharacterExpertisesInput from "@dndComponents/character/features/CharacterExpertisesInput";

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
// const common_util = DataUtilities.getInstance();

interface Props {
  obj: CharacterFeature;
  source: CharacterFeatureSource;
  character: Character;
  show_source?: boolean;
}

export default function CharacterFeatureInput({ 
  obj,
  source,
  character,
  show_source = false,
}: Props) {
  const [show_source_modal, setShowSourceModal] = useState(false);

  const onChange = () => {
    character.changed();
    character.subscription_holder.recalculate();
  }

  // debugger;
  const enabled = api.user && (character.owner_id === api.user._id || (character.campaign !== null && character.campaign.owner_id === api.user._id));
  if (obj.feature) {
    // switch based on feature type
    // debugger;
    let details = 
      <Grid size={12}
        container spacing={0} direction="row">
        <Grid size={12} style={{ fontWeight: "bold" }}>
          { obj.feature.name }
        </Grid>
        <Grid size={12}>
          <DisplayStringWithReplacements
            display_string={obj.feature.description}
          />
        </Grid>
        { obj.feature_options.map((opt, key) => {
          if (opt.features) {
            return (
              <Grid size={12} key={key}
                container spacing={0} direction="row">
                { opt.features.map((cf: any, key2: any) => {
                  const char_feature = cf as CharacterFeature;
                  return (
                    <Grid size={12} key={key2}>
                      { char_feature.name } { char_feature.feature && 
                        <span><DisplayStringWithReplacements display_string={ char_feature.feature.description } /></span>
                      }
                    </Grid>
                  );
                })}
              </Grid>
            );
          } else {
            return null;
          }
        })}
      </Grid>;
    if (enabled) {
      switch(obj.feature_type) {
        case "Subclass":
          details = (
            <Grid size={12}>
              <CharacterSubclassInput 
                character={character} 
                obj={obj} 
                onChange={(changed: CharacterFeature) => {
                  onChange();
                }}
              />
            </Grid>
          );
        break;
        case "Subrace":
          details = (
            <Grid size={12}>
              <CharacterSubraceInput 
                character={character} 
                obj={obj} 
                onChange={(changed: CharacterFeature) => {
                  onChange();
                }}
              />
            </Grid>
          );
        break;
        case "Language":
          if (obj.feature_options[0] instanceof CharacterLanguageFeature && 
            obj.floating_feature instanceof LanguageFeature) {
            const clf = obj.feature_options[0];
            const lf = obj.floating_feature;
            // console.log(clf);
            // console.log(lf);
            details = 
              <Grid size={12}>
                <CharacterLanguageFeatureInput
                  character={character}
                  obj={obj}
                  color={clf.language_ids.length !== lf.count ? "blue" : ""}
                  onChange={(changed: CharacterLanguageFeature) => {
                    obj.feature_options[0] = changed;
                    onChange();
                  }}
                />
              </Grid>;
          }
        break;
        case "Specific Special Feature":
        case "Special Feature Choices":
        case "Special Feature":
          if (obj.floating_feature instanceof SFFeature) {
            const sff = obj.floating_feature;
            const spec_choices = sff.special_feature_ids;
            details = 
              <Grid size={12}>
                <CharacterSpecialFeatureBox
                  char={character}
                  type={sff.special_feature_type}
                  source_id={obj.use_source_id}
                  obj={obj}
                  options={spec_choices}
                  onChange={() => {
                    onChange();
                  }}
                />
              </Grid>;
          }
        break;
        case "Special Feature List Merge":
          details = 
            <Grid size={12}>
              You will have extra options available.
            </Grid>;
        break;
        case "Sense":
          const sense = obj.feature_options[0] as CharacterSense;
          if (sense) {
            details = 
              <Grid size={12}>
                { sense.name }: { sense.range }
              </Grid>;
          }
        break;
        case "Pact Boon":
          details = 
            <Grid size={12}>
              <CharacterPactBoonBox
                character={character}
                obj={obj}
                onChange={() => {
                  onChange();
                }}
              />
            </Grid>;
        break;
        case "Eldritch Invocations":
          details = 
            <Grid size={12}>
              <CharacterEldritchInvocationsBox
                character={character}
                obj={obj}
                onChange={() => {
                  onChange();
                }}
              />
            </Grid>;
        break;
        case "Weapon Mastery":
          // console.log("weapon mastery");
          details = 
            <Grid size={12}>
              <CharacterWeaponMasteriesBox
                character={character}
                obj={obj}
                onChange={() => {
                  onChange();
                }}
              />
            </Grid>;
        break;
        case "Fighting Style":
          details = 
            <Grid size={12}>
              <CharacterFightingStyleBox
                character={character}
                obj={obj}
                onChange={() => {
                  onChange();
                }}
              />
            </Grid>;
        break;
        // case "Cantrips from List":
        case "Spells from List":
          if (obj.floating_feature && 
            obj.floating_feature instanceof SpellsFromList) {
            details = (
              <Grid size={12}>
                <CharacterSpellsFromListInput
                  character={character}
                  char_feature={obj}
                  onChange={onChange}
                />
              </Grid>
            );
          }
        break;
        case "Proficiencies":
          if (obj.floating_feature && 
            obj.floating_feature instanceof Proficiency) {
            // console.log(obj);
            details = (
              <>
                <Grid size={12}>
                  <CharacterProficienciesInput 
                    character={character}
                    char_feature={obj}
                  />
                </Grid>
              </>
            );
          }
        break;
        case "Expertise":
          if (obj.floating_feature && 
            obj.floating_feature instanceof Proficiency) {
            details = (
              <>
                <Grid size={12}>
                  <CharacterExpertisesInput 
                    character={character}
                    char_feature={obj}
                    onChange={() => {
                      onChange();
                    }}
                  />
                </Grid>
              </>
            );
          }
        break;
        case "Mystic Arcanum":
          details = 
            <Grid size={12}>
              { obj.feature_options.map((opt, key) => {
                return (
                  <span key={key}>Mystic Arcanum {opt.level}</span>
                );
              })}
            </Grid>;
        break;
        case "Spell Mastery":
          details = 
            <Grid size={12}>
              { obj.feature_options.map((opt, key) => {
                return (
                  <span key={key}>Spell Mastery {opt.level}</span>
                );
              })}
            </Grid>;
        break;
        case "Feat":
          details = 
            <Grid size={12}>
              <CharacterFeatBox
                char={character}
                obj={obj}
                onChange={() => {
                  // obj.feature_options[0] = changed;
                  onChange();
                }}
              />
            </Grid>;
        break;
        case "Selectable":
          if (obj.floating_feature instanceof Selectable) {
            details = 
              <Grid size={12}>
                <CharacterSelectableInput
                  character={character}
                  obj={obj} 
                  onChange={(changed: string[]) => {
                    obj.feature_options = changed;
                    onChange();
                  }}
                />
              </Grid>;
          }
        break;
        case "Ability Score Improvement":
          // console.log(obj);
          details = 
            <Grid size={12}>
              <CharacterASIBaseFeatureInput
                character={character}
                obj={obj}
              />
            </Grid>;
        break;
        case "Characteristic":
          details = 
            <CharacterCharacteristicInput 
              character={character}
              char_feature={obj}
              // value={obj.feature_options[0]}
              onChange={(changed: string) => {
                obj.feature_options[0] = changed;
                onChange();
              }}
            />;
        break;
        case "Knowledge":
          details = 
            <CharacterKnowledgeFeatureInput 
              character={character} 
              obj={obj} 
              onChange={() => {
                onChange();
              }}
            />;
        break;
        case "Bonus Spells":
          if (obj.floating_feature && 
            obj.floating_feature instanceof BonusSpells) {
            details = (
              <Grid size={12}>
                <CharacterBonusSpellsInput
                  character={character}
                  char_feature={obj}
                  onChange={(changed: CharacterBonusSpells) => {
                    // debugger;
                    obj.feature_options = [changed];
                    onChange();
                  }}
                />
              </Grid>
            );
          }
        break;
        default:
          details = 
            <Grid
              size={12}>
              <b>{ obj.feature.name }</b> <DisplayStringWithReplacements display_string={ obj.feature.description } />
            </Grid>;
        break;
      }
    }
    return (
      <Grid container spacing={0} direction="row">
        { details }
        { show_source &&
          <Grid size={12}
            style={{ 
              fontWeight: "bold", 
              cursor: "pointer" 
            }}
            onClick={() => {
              setShowSourceModal(true);
            }}>
            { char_util.getUseName(source) }
          </Grid>
        }
        <ModalOrDrawer
          open={show_source_modal}
          onClose={() => { setShowSourceModal(false) }}
        >
          <Grid container spacing={0} direction="row">
            <Grid size={12}
              className="centered"
              style={{ fontWeight: "bold" }}>
              { char_util.getUseName(source) }
            </Grid>
            <Grid size={12}>
              <CharacterFeatureSourceInput
                character={character}
                source={source}
                // onChange={(do_main: boolean) => {
                //   if (onSourceChange) {
                //     onSourceChange(source, do_main);
                //   }
                // }}
              />
            </Grid>
          </Grid>
        </ModalOrDrawer>
      </Grid>
    );
  }
  return <></>;
}
